import React, { useState, useEffect } from 'react';
import { withStyles, useTheme } from '@material-ui/core';
import { Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@material-ui/core';
import { ENTITY_PROPS, MESSAGES } from './AccessGroupsTable';
import Swal from 'sweetalert2';
import { del, get, handleResponse, patch, post, swal500, handle400 } from '../../utils/network';
import styles from '../Documents/DocumentsTypeForm/styles';

import {
    useFormValidation,
    useFormContainer,
    FormLayout,
    TextInput,
} from '../common';
import validateGroup from './validator';


const FIELDS = {
    group: 'Nombre del grupo',
    entraIdGroupId: 'ID de Entra ID',
    documentTypesPermissions: 'Permisos de tipos de documento',
    featuresPermissions: 'Permisos de funcionalidades',
};

const INITIAL_STATE = {
    group: '',
    entraIdGroupId: '',
    documentTypesPermissions: [],
    featuresPermissions: [],
};


const AccessGroupsFormComponent = ({ match, history, classes }) => {
    const { id } = match.params;
    const { entityName, endpoint, path } = ENTITY_PROPS;
    const [documentTypes, setDocumentTypes] = useState([]);
    const [featuresPermissions, setFeaturesPermissions] = useState([]);
    const theme = useTheme();

    const { handleSubmit, handleChange, handleBlur, values, errors, setErrors, resetValues } =
        useFormValidation(INITIAL_STATE, values => validateGroup(values));

    const {
        data,
        setData,
        editMode,
        setEditMode,
        loading,
        setLoading,
        submitting,
        setSubmitting,
        isNew,
        handleClose,
        handleCancel,
    } = useFormContainer(ENTITY_PROPS, resetValues, MESSAGES.deleteConfirmation(values));


    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchDocumentTypes(),
            fetchFeaturesPermissions(),
        ]).then((results) => {
            getGroup(results[0], results[1]);
        });
    }, [id, endpoint, history, path, resetValues, setData, setEditMode, setLoading]);

    //Para obtener el grupo
    const getGroup = (fetchedDocumentTypes, fetchedFeaturesPermissions) => {
        if (id !== 'new') {
            return get(`${endpoint}/${id}`)
                .then(res => handleResponse(res, { history }))
                .then(resGroup => {
                    setData(resGroup.message);
                    resetValues(resGroup.message);

                    const documentTypesPermissions = resGroup.message.documentTypesPermissions || [];
                    const documentTypesWithPermissions = fetchedDocumentTypes.map((documentType) => {
                        const permission = documentTypesPermissions.find((perm) => perm._id === documentType._id)
                        return {
                            ...documentType,
                            read: permission?.read || false,
                            write: permission?.write || false,
                        }
                    });
                    setDocumentTypes(documentTypesWithPermissions);

                    const featuresPermissions = resGroup.message.featuresPermissions || [];
                    const featuresPermissionsWithEnabledFlag = fetchedFeaturesPermissions.map((feature) => {
                        const permission = featuresPermissions.find((perm) => perm.code === feature.code)
                        return {
                            ...feature,
                            enabled: permission?.enabled || false,
                        }
                    });
                    setFeaturesPermissions(featuresPermissionsWithEnabledFlag);
                })
                .then(res => {
                    setLoading(false);
                })
                .catch(err => {
                    swal500(err);
                    history.push(path);
                });
        } else {
            const documentTypesWithPermissions = fetchedDocumentTypes.map((documentType) => ({
                ...documentType,
                read: false,
                write: false,
                
            }));
            setDocumentTypes(documentTypesWithPermissions);

            const featuresWithPermissions = fetchedFeaturesPermissions.map((feature) => ({
                ...feature,
                enabled: false,
            }));
            setFeaturesPermissions(featuresWithPermissions);

            setLoading(false);
            setEditMode(true);
            return;
        }
    };

    //Obtiene todos los tipos de documento
    const fetchDocumentTypes = () => {
        return get('documentTypes')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Mensaje error');
                }
                return response.json();
            })
            .then(data => {
                return data.message.data;
            })
            .catch(error => {
                console.error('Mensaje error', error);
            });
    };

    const fetchFeaturesPermissions = () => {
        return get('features-permissions')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Mensaje error');
                }
                return response.json();
            })
            .then(data => {
                return data.message;
            })
            .catch(error => {
                console.error('Mensaje error', error);
            });
    };

    const handleResult = e => {
            setSubmitting(true);
            const requestBody = {
                group: values.group,
                entraIdGroupId: values.entraIdGroupId,
                documentTypesPermissions: values.documentTypesPermissions,
                featuresPermissions: values.featuresPermissions,
            };
            if (isNew) {
                post(endpoint, requestBody)
                    .then(res =>{
                        return handleResponse(res, { history }, [
                            { status: 400, method: handle400(FIELDS, setErrors) },
                        ])}
                    )
                    .then(() => history.push({ pathname: path, state: { msg: 'created' } }))
                    .catch(err => err !== 400 && err !== 409 && swal500(err))
                    .finally(() => setSubmitting(false));
            } else {
                patch(`${endpoint}/${id}`, requestBody)
                    .then(res =>
                        handleResponse(res, { history }, [])
                    )
                    .then(() => history.push({ pathname: path, state: { msg: 'edited' } }))
                    .catch(err => err !== 400 && err !== 409 && swal500(err))
                    .finally(() => setSubmitting(false));
            }
    };

    const getChangedValues = values => {
        const changedValues = [];
        Object.keys(data).forEach(key => data[key] !== values[key] && changedValues.push(key));
        return changedValues;
    };

    //Actualiza los values para permisos de tipos de documento
    const updateDocumentTypesPermissions = (updatedDocumentTypes) => {
        setDocumentTypes(updatedDocumentTypes);

        const updatedPermissions = updatedDocumentTypes.map(documentType => ({
            _id: documentType._id,
            documentType: documentType.documentType,
            read: documentType.read,
            write: documentType.write
        }));

        handleChange({ target: { name: 'documentTypesPermissions', value: updatedPermissions } });
    };

    // Actualiza los values para permisos de funcionalidades
    const updateFeaturePermissions = (updatedFeaturesPermissions) => {
        setFeaturesPermissions(updatedFeaturesPermissions);

        const updatedPermissions = updatedFeaturesPermissions.map(featurePermission => ({
            code: featurePermission.code,
            name: featurePermission.name,
            enabled: featurePermission.enabled,
        }));

        handleChange({ target: { name: 'featuresPermissions', value: updatedPermissions } });
    }

    // seleccionar los permisos de un tipo para todos los tipos de documento
    const handleSelectAllDocumentTypeChange = (permissionType, e) => {
        const checked = e.target.checked;
        const updatedDocumentTypes = documentTypes.map(documentType => ({
            ...documentType,
            [permissionType]: checked,
        }));
        updateDocumentTypesPermissions(updatedDocumentTypes);
    };

    // seleccionar los permisos de un tipo para todas las funcionalidades
    const handleSelectAllFeaturesPermissionsChange = (e) => {
        const checked = e.target.checked;
        const updatedFeaturesPermissions = featuresPermissions.map(featurePermission => ({
            ...featurePermission,
            enabled: checked,
        }));
        updateFeaturePermissions(updatedFeaturesPermissions);
    };

    const hasChanges = () => editMode && getChangedValues(values).length > 0;


    //Verifica si el checkbox que corresponde al tipo de documento y al permiso
    //que recibe por parametro tiene que estar checkeado
    const isDocumentTypeChecked = (documentType, permission) => {
        if (values.documentTypesPermissions) {
            const groupPermission = values.documentTypesPermissions.find(perm => perm.documentType === documentType);
            if (groupPermission) {
                return groupPermission[permission];
            }
        }

        return false;
    };

    const isFeaturePermissionChecked = (featurePermission) => {
        if (values.featuresPermissions) {
            const permission = values.featuresPermissions.find(perm => perm.code === featurePermission.code);
            if (permission) {
                return permission["enabled"];
            }
        }

        return false;
    }

    //Actualizar el estado de un checkbox
    const handlePermissionChange = (documentTypeId, permissionType, value) => {
        setDocumentTypes(prevDocumentTypes => {
            const updatedDocumentTypes = prevDocumentTypes.map(documentType => {
                if (documentType._id === documentTypeId) {
                    return {
                        ...documentType,
                        [permissionType]: value
                    };
                }
                return documentType;
            });
            updateDocumentTypesPermissions(updatedDocumentTypes);

            return updatedDocumentTypes;
        });
    };

    //Actualizar el estado de un checkbox de funcionalidad
    const handleFeaturePermissionChange = (featurePermissionCode, value) => {
        setFeaturesPermissions(prevFeaturesPermissions => {
            const updatedFeaturesPermissions = prevFeaturesPermissions.map(featurePermission => {
                if (featurePermission.code === featurePermissionCode) {
                    return {
                        ...featurePermission,
                        enabled: value
                    };
                }
                return featurePermission;
            });
            updateFeaturePermissions(updatedFeaturesPermissions);

            return updatedFeaturesPermissions;
        });
    };

    const handleDelete = id => {
        const item = data._id
        Swal.fire({
          title: `Eliminar ${entityName}`,
          text: MESSAGES.deleteConfirmation(item),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: theme.palette.primary.main,
          cancelButtonText: `No, conservar ${entityName}`,
          confirmButtonText: 'Si, estoy seguro',
          width: '34em',
        }).then(result => {
          if (result.value) {
            del(`${endpoint}/${item}`)
              .then(res => handleResponse(res, { history }))
              .then(() => history.push({ pathname: path, state: { msg: 'deleted' } }))
              .catch(err => {
                swal500(err);
              });
          }
        });
      };

    return (
        <FormLayout
            handleClose={handleClose}
            loading={loading}
            editMode={editMode}
            submitting={submitting}
            handleSubmit={handleResult}
            handleCancel={handleCancel}
            handleEdit={() => setEditMode(true)}
            handleDelete={handleDelete}
            isNew={isNew}
            entityName={entityName}
            hasChanges={hasChanges}
        >
            <TextInput
                name="group"
                error={errors.group}
                value={values.group}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={FIELDS['group']}
                disabled={!editMode}
            />
            <TextInput
                name="entraIdGroupId"
                error={errors.entraIdGroupId}
                value={values.entraIdGroupId}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={FIELDS['entraIdGroupId']}
                disabled={!editMode}
            />
            <Typography
                style={{
                    fontSize: 16,
                    margin: '15px 0',
                }}
            >
                {FIELDS['featuresPermissions']}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Funcionalidad</TableCell>
                            <TableCell>Habilitado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                Todos
                            </TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={featuresPermissions.every(f => f.enabled)}
                                    indeterminate={featuresPermissions.some(f => f.enabled) && featuresPermissions.some(f => !f.enabled)}
                                    onChange={e => handleSelectAllFeaturesPermissionsChange(e)}
                                    disabled={!editMode}
                                />
                            </TableCell>
                        </TableRow>
                        {featuresPermissions.map((featurePermission, index) => (
                            <TableRow key={featurePermission.code}>
                                <TableCell>{featurePermission.name}</TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={featurePermission.enabled || isFeaturePermissionChecked(featurePermission)}
                                        onChange={e => handleFeaturePermissionChange(featurePermission.code, e.target.checked)}
                                        disabled={!editMode}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography
                style={{
                    fontSize: 16,
                    margin: '15px 0',
                }}
            >
                {FIELDS['documentTypesPermissions']}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Tipo de Documento</TableCell>
                            <TableCell>Lectura</TableCell>
                            <TableCell>Escritura</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                Todos
                            </TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={documentTypes.every(p => p.read)}
                                    indeterminate={documentTypes.some(p => p.read) && documentTypes.some(p => !p.read)}
                                    onChange={e => handleSelectAllDocumentTypeChange('read', e)}
                                    disabled={!editMode}
                                />
                            </TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={documentTypes.every(p => p.write)}
                                    indeterminate={documentTypes.some(p => p.write) && documentTypes.some(p => !p.write)}
                                    onChange={e => handleSelectAllDocumentTypeChange('write', e)}
                                    disabled={!editMode}
                                />
                            </TableCell>
                        </TableRow>

                        {documentTypes.map((documentType) => (
                            <TableRow key={documentType.documentType}>
                                <TableCell>{documentType.documentType}</TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={documentType.read || isDocumentTypeChecked(documentType, 'read')}
                                        onChange={e => handlePermissionChange(documentType._id, 'read', e.target.checked)}
                                        disabled={!editMode}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={documentType.write || isDocumentTypeChecked(documentType, 'write')}
                                        onChange={e => handlePermissionChange(documentType._id, 'write', e.target.checked)}
                                        disabled={!editMode}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}

                    </TableBody>
                </Table>
            </TableContainer>
            {editMode && <div className={classes.divider} />}
        </FormLayout>
    );
};

export default withStyles(styles)(AccessGroupsFormComponent);
